import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Alert,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Skeleton,
  Fab,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as EngineeringIcon,
  Construction as ConstructionIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Lock as LockIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  AddTask as AddTaskIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { usersService, authService } from '../services/api';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
      {icon && React.cloneElement(icon, { sx: { color: color || 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>{value}</Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>{label}</Typography>
  </Box>
);

// Componente FormSection
const FormSection = ({ icon, title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon && React.cloneElement(icon, { sx: { color: '#e50000', fontSize: '1.3rem' } })}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>{title}</Typography>
    </Box>
    {children}
  </Box>
);

// Il permesso vale solo per chi ha il ruolo capocantiere (admin e CapoCommessa creano già interventi)
const puoCreareInterventi = (utente) => {
  const roles = (utente?.roles && utente.roles.length) ? utente.roles : [utente?.role].filter(Boolean);
  return !!utente?.puo_creare_interventi && roles.includes('capocantiere');
};

const ChipCreaInterventi = () => (
  <Chip
    icon={<AddTaskIcon sx={{ fontSize: '0.85rem' }} />}
    label="Crea interventi"
    size="small"
    variant="outlined"
    sx={{
      height: 20,
      fontSize: '0.65rem',
      fontWeight: 600,
      color: '#2e7d32',
      borderColor: 'rgba(46,125,50,0.5)',
      '& .MuiChip-icon': { color: '#2e7d32', ml: 0.5 },
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
);

const Utenti = () => {
  const { user: currentLoggedUser } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('tutti');
  const [searchFocused, setSearchFocused] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'tecnico',
    nome: '',
    cognome: '',
    telefono: '',
    puo_creare_interventi: false,
  });

  useEffect(() => {
    loadUtenti();
  }, []);

  const loadUtenti = async () => {
    try {
      setLoading(true);
      const response = await usersService.getAll();
      setUtenti(response.data);
    } catch (error) {
      setError('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setCurrentUser(user);
      setFormData({
        ...user,
        roles: (user.roles && user.roles.length) ? user.roles : [user.role].filter(Boolean),
        puo_creare_interventi: !!user.puo_creare_interventi,
        password: '',
      });
    } else {
      setCurrentUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'tecnico',
        roles: ['tecnico'],
        nome: '',
        cognome: '',
        telefono: '',
        puo_creare_interventi: false,
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentUser(null);
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Il gestore password del browser, vedendo i campi username/password della dialog, tentava di
  // autocompilare le credenziali salvate e finiva per scrivere nel campo "Cerca" della pagina
  // (primo input di testo del documento) → la lista si ritrovava filtrata da sola. Mentre la
  // dialog è aperta i filtri non sono raggiungibili dall'utente: qualunque modifica qui arriva
  // dal browser, quindi la ignoriamo. (Gli attributi autoComplete della dialog fanno il resto.)
  const handleSearchChange = (e) => {
    if (dialogOpen) return;
    setSearchQuery(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    if (dialogOpen) return;
    setRoleFilter(e.target.value);
  };

  const handleSubmit = async () => {
    try {
      if (currentUser) {
        const dataToSend = { ...formData };
        if (!dataToSend.password) {
          delete dataToSend.password;
        }
        await usersService.update(currentUser.id, dataToSend);
      } else {
        await authService.register(formData);
      }
      loadUtenti();
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await usersService.delete(userToDelete.id);
      loadUtenti();
      handleCloseDeleteDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon />;
      case 'amministratore':
        return <AdminIcon />;
      case 'capocantiere':
        return <EngineeringIcon />;
      case 'tecnico':
        return <ConstructionIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return { bg: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f', border: '#d32f2f' };
      case 'amministratore':
        return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9c27b0', border: '#9c27b0' };
      case 'capocantiere':
        return { bg: 'rgba(255, 152, 0, 0.1)', color: '#f57c00', border: '#f57c00' };
      case 'tecnico':
        return { bg: 'rgba(33, 150, 243, 0.1)', color: '#1976d2', border: '#1976d2' };
      default:
        return { bg: 'rgba(158, 158, 158, 0.1)', color: '#757575', border: '#757575' };
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Super Admin';
      case 'amministratore':
        return 'CapoCommessa';
      case 'capocantiere':
        return 'Capocantiere';
      case 'tecnico':
        return 'Tecnico';
      default:
        return role;
    }
  };

  // Statistiche
  const stats = {
    totale: utenti.length,
    admin: utenti.filter((u) => u.role === 'admin').length,
    amministratori: utenti.filter((u) => u.role === 'amministratore').length,
    capocantieri: utenti.filter((u) => u.role === 'capocantiere').length,
    tecnici: utenti.filter((u) => u.role === 'tecnico').length,
  };

  // Filtra utenti
  const filteredUtenti = utenti.filter((utente) => {
    if (roleFilter !== 'tutti' && utente.role !== roleFilter) {
      return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      utente.username?.toLowerCase().includes(query) ||
      utente.nome?.toLowerCase().includes(query) ||
      utente.cognome?.toLowerCase().includes(query) ||
      utente.email?.toLowerCase().includes(query) ||
      utente.telefono?.toLowerCase().includes(query) ||
      utente.role?.toLowerCase().includes(query) ||
      `${utente.nome} ${utente.cognome}`.toLowerCase().includes(query)
    );
  });

  // Reset pagina quando cambiano i filtri
  useEffect(() => {
    setPage(0);
  }, [searchQuery, roleFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUtenti = filteredUtenti.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header con gradiente scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 2, sm: 3 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          {/* Titolo e pulsante */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  mb: 0.5,
                }}
              >
                Gestione Utenti
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Amministra gli utenti del sistema
              </Typography>
            </Box>
            {!isMobile && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  bgcolor: '#e50000',
                  color: '#fff',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                  '&:hover': {
                    bgcolor: '#cc0000',
                    boxShadow: '0 6px 20px rgba(229,0,0,0.5)',
                  },
                }}
              >
                Nuovo Utente
              </Button>
            )}
          </Box>

          {/* Statistiche */}
          {loading ? (
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rectangular" width={80} height={60} sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 2, sm: 4, md: 6 },
                flexWrap: 'wrap',
              }}
            >
              <StatBox icon={<GroupsIcon />} value={stats.totale} label="Totale Utenti" />
              <Box sx={{ width: '1px', bgcolor: 'rgba(255,255,255,0.2)', display: { xs: 'none', sm: 'block' } }} />
              <StatBox icon={<AdminIcon />} value={stats.admin} label="Super Admin" color="#d32f2f" />
              <StatBox icon={<AdminIcon />} value={stats.amministratori} label="CapoCommessa" color="#9c27b0" />
              <StatBox icon={<EngineeringIcon />} value={stats.capocantieri} label="Capocantieri" color="#f57c00" />
              <StatBox icon={<ConstructionIcon />} value={stats.tecnici} label="Tecnici" color="#1976d2" />
            </Box>
          )}
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: 3, pb: { xs: 10, sm: 4 } }}>
        {/* Barra di ricerca e filtri */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Cerca per username, nome, email, telefono..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                size="small"
                name="ricerca-utenti"
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: searchFocused ? '#e50000' : 'text.secondary', transition: 'color 0.2s' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#e50000' },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e50000',
                        borderWidth: 2,
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                value={roleFilter}
                onChange={handleRoleFilterChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e50000',
                    },
                  },
                }}
              >
                <MenuItem value="tutti">Tutti i Ruoli</MenuItem>
                <MenuItem value="admin">Super Admin</MenuItem>
                <MenuItem value="amministratore">CapoCommessa</MenuItem>
                <MenuItem value="capocantiere">Capocantiere</MenuItem>
                <MenuItem value="tecnico">Tecnico</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Risultati trovati */}
          {(searchQuery || roleFilter !== 'tutti') && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredUtenti.length} utent{filteredUtenti.length === 1 ? 'e' : 'i'} trovato{filteredUtenti.length === 1 ? '' : 'i'}
              </Typography>
              {(searchQuery || roleFilter !== 'tutti') && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setRoleFilter('tutti');
                  }}
                  sx={{ color: '#e50000', textTransform: 'none' }}
                >
                  Resetta filtri
                </Button>
              )}
            </Box>
          )}
        </Paper>

        {/* Lista utenti */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={isMobile ? 180 : 60} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
        ) : filteredUtenti.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <PersonIcon sx={{ fontSize: 64, color: 'rgba(0,0,0,0.2)', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {searchQuery || roleFilter !== 'tutti' ? 'Nessun utente trovato' : 'Nessun utente nel sistema'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || roleFilter !== 'tutti'
                ? 'Prova a modificare i filtri di ricerca'
                : 'Clicca su "Nuovo Utente" per aggiungere il primo utente'}
            </Typography>
          </Paper>
        ) : isMobile ? (
          /* Vista Mobile - Cards */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedUtenti.map((utente) => {
              const roleColors = getRoleColor(utente.role);
              return (
                <Card
                  key={utente.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#e50000',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box flex={1}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, mb: 0.5, color: '#1a1a1a' }}>
                          {utente.nome} {utente.cognome}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          @{utente.username}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                          icon={React.cloneElement(getRoleIcon(utente.role), { sx: { fontSize: '1rem' } })}
                          label={getRoleLabel(utente.role)}
                          size="small"
                          sx={{
                            bgcolor: roleColors.bg,
                            color: roleColors.color,
                            border: `1px solid ${roleColors.border}`,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: roleColors.color },
                          }}
                        />
                        {puoCreareInterventi(utente) && <ChipCreaInterventi />}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2">{utente.email}</Typography>
                      </Box>
                      {utente.telefono && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                          <Typography variant="body2">{utente.telefono}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Registrato il {new Date(utente.created_at).toLocaleDateString('it-IT')}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(utente)}
                        sx={{
                          color: '#e50000',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Modifica
                      </Button>
                      {utente.id !== currentLoggedUser?.id && (
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleOpenDeleteDialog(utente)}
                          sx={{
                            color: '#f44336',
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          Elimina
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Vista Desktop - Tabella */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Utente</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Telefono</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Ruolo</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Data Registrazione</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1a1a1a' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUtenti.map((utente) => {
                  const roleColors = getRoleColor(utente.role);
                  return (
                    <TableRow
                      key={utente.id}
                      sx={{
                        transition: 'background-color 0.2s',
                        '&:hover': { bgcolor: 'rgba(229,0,0,0.02)' },
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                            {utente.nome} {utente.cognome}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            @{utente.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{utente.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{utente.telefono || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                          <Chip
                            icon={React.cloneElement(getRoleIcon(utente.role), { sx: { fontSize: '1rem' } })}
                            label={getRoleLabel(utente.role)}
                            size="small"
                            sx={{
                              bgcolor: roleColors.bg,
                              color: roleColors.color,
                              border: `1px solid ${roleColors.border}`,
                              fontWeight: 600,
                              '& .MuiChip-icon': { color: roleColors.color },
                            }}
                          />
                          {puoCreareInterventi(utente) && <ChipCreaInterventi />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(utente.created_at).toLocaleDateString('it-IT')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(utente)}
                            sx={{
                              color: '#e50000',
                              '&:hover': { bgcolor: 'rgba(229,0,0,0.1)' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {utente.id !== currentLoggedUser?.id && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(utente)}
                              sx={{
                                color: '#f44336',
                                '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Paginazione */}
        {filteredUtenti.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TablePagination
              component="div"
              count={filteredUtenti.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe per pagina:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
              sx={{
                bgcolor: '#fafafa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Paper>
        )}
      </Container>

      {/* FAB per mobile */}
      {isMobile && (
        <Fab
          onClick={() => handleOpenDialog()}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            bgcolor: '#e50000',
            color: '#fff',
            '&:hover': { bgcolor: '#cc0000' },
            boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog Crea/Modifica Utente */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header del dialog */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#e50000' }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              {currentUser ? 'Modifica Utente' : 'Nuovo Utente'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <FormSection icon={<BadgeIcon />} title="Credenziali di Accesso">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="username"
                  label="Username"
                  fullWidth
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  disabled={currentUser}
                  size="small"
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email"
                  fullWidth
                  required
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  size="small"
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {/* autoComplete="new-password": impedisce a Chrome di riversare qui la password salvata
                    dell'admin, che verrebbe scritta sull'utente modificato senza che nessuno se ne accorga */}
                <TextField
                  name="password"
                  label={currentUser ? 'Nuova Password (lascia vuoto per non modificare)' : 'Password'}
                  fullWidth
                  required={!currentUser}
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  size="small"
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection icon={<PersonIcon />} title="Dati Personali">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="nome"
                  label="Nome"
                  fullWidth
                  required
                  value={formData.nome}
                  onChange={handleInputChange}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="cognome"
                  label="Cognome"
                  fullWidth
                  required
                  value={formData.cognome}
                  onChange={handleInputChange}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="telefono"
                  label="Telefono"
                  fullWidth
                  value={formData.telefono}
                  onChange={handleInputChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="roles"
                  label="Ruoli"
                  fullWidth
                  required
                  value={formData.roles || (formData.role ? [formData.role] : [])}
                  onChange={handleInputChange}
                  size="small"
                  helperText="Puoi assegnare più ruoli: al login l'utente sceglie con quale entrare"
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (selected || []).map((r) => getRoleLabel(r)).join(', '),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e50000' },
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e50000' },
                  }}
                >
                  <MenuItem value="tecnico">Tecnico</MenuItem>
                  <MenuItem value="capocantiere">Capocantiere</MenuItem>
                  <MenuItem value="amministratore">CapoCommessa</MenuItem>
                  <MenuItem value="admin">Super Admin</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </FormSection>

          {/* Permesso extra: solo per chi ha il ruolo capocantiere (admin e CapoCommessa creano già interventi) */}
          {(formData.roles || []).includes('capocantiere') && (
            <FormSection icon={<AddTaskIcon />} title="Permessi">
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: formData.puo_creare_interventi ? '#e50000' : 'divider',
                  bgcolor: formData.puo_creare_interventi ? 'rgba(229,0,0,0.03)' : 'transparent',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  transition: 'all 0.2s',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={!!formData.puo_creare_interventi}
                      onChange={(e) => setFormData({ ...formData, puo_creare_interventi: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#e50000' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#e50000' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                      Può creare interventi
                    </Typography>
                  }
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                  Gli fa comparire il pulsante "Nuovo Intervento": potrà pianificare interventi assegnandoli a sé
                  o a un altro capocantiere. Modifica ed eliminazione restano al CapoCommessa.
                </Typography>
              </Box>
            </FormSection>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#e50000',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: '#cc0000' },
            }}
          >
            {currentUser ? 'Salva Modifiche' : 'Crea Utente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ color: '#fff' }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              Conferma Eliminazione
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDeleteDialog} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(244,67,54,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <PersonIcon sx={{ fontSize: 40, color: '#f44336' }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Eliminare l'utente "{userToDelete?.username}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {userToDelete?.nome} {userToDelete?.cognome}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userToDelete?.email}
            </Typography>
            <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
              Questa azione è irreversibile. Tutti i dati associati a questo utente verranno eliminati.
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              bgcolor: '#f44336',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: '#d32f2f' },
            }}
          >
            Elimina Utente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Utenti;
